// Estimating service additions made when the app starts: edit versions (see the estimating_edit_versions migration), the stale count, and the change history of a custom estimate.
// Edit versions: each estimate has an edit counter that every saved edit moves forward.
// This wrapper remembers the counter an estimate had when it was opened and sends it with each save; the database refuses a save made from an out-of-date copy
// (someone else has changed the estimate since), with a message saying who changed it and when. The remembered counter moves forward only with this person's own saves,
// so reading an estimate again never hides someone else's change. An estimate that hasn't been opened yet sends no version, which skips the check.
export function withEditVersions(service, sb) {
  const est = () => sb.schema("estimating");
  const versions = {};
  const ver = id => (Object.prototype.hasOwnProperty.call(versions, id) ? versions[id] : null);
  const remember = (id, v) => { if (v != null && isFinite(Number(v))) versions[id] = Number(v); };
  const rpc = async (fn, args, id) => {
    const { data, error } = await est().rpc(fn, Object.assign({}, args, { p_version: ver(id) }));
    if (error) throw { code: "upstream_error", message: error.message };
    if (data && data.version != null) remember(id, data.version);
    return data;
  };
  return Object.assign({}, service, {
    // opening an estimate takes its current version as the one the person is working from
    async getEstimate(id) {
      const data = await service.getEstimate(id);
      if (data) remember(id, data.edit_version);
      return data;
    },
    saveEp: (id, section, changes) => rpc("save_ep", { p_estimate: id, p_section: section, p_changes: changes }, id),
    refreshEp: id => rpc("refresh_ep", { p_estimate: id }, id),
    saveFloor: (id, state) => rpc("save_floor", { p_estimate: id, p_state: state }, id),
    saveWet: (id, rows) => rpc("save_wet", { p_estimate: id, p_rows: rows }, id),
    saveOnsite: (id, state) => rpc("save_onsite", { p_estimate: id, p_state: state }, id),
    saveVerandahs: (id, rows, doubleGable) => rpc("save_verandahs", { p_estimate: id, p_rows: rows, p_gable: doubleGable || 0 }, id),
    saveRoomCounts: (id, counts) => rpc("save_room_counts", { p_estimate: id, p_counts: counts }, id),
    saveModules: (id, rows) => rpc("save_modules", { p_estimate: id, p_rows: rows }, id),
    setLineAdjustments: (id, changes) => rpc("set_line_adjustments", { p_estimate: id, p_changes: changes }, id),
    async updateEstimate(id, changes) {
      const { error } = await est().rpc("update_estimate", { p_id: id, p_changes: changes || {}, p_version: ver(id) });
      if (error) throw { code: "upstream_error", message: error.message };
      // this save is an edit too, so take the new version
      const r = await est().from("estimates").select("edit_version").eq("id", id).single();
      if (r && r.data) remember(id, r.data.edit_version);
    },
    // the history of changes made to a custom estimate's tables, newest first (the log the database keeps for each save)
    async getCustomLog(id) {
      const { data, error } = await est().from("estimate_custom_log").select("id, action, area, reason, before_rows, after_rows, changed_by, changed_by_name, changed_at")
        .eq("estimate_id", id).order("changed_at", { ascending: false }).limit(200);
      if (error) throw { code: "upstream_error", message: error.message };
      return data || [];
    },
    // how many templates and drafts are waiting for a recalculation
    async getStaleCount() {
      const { data, error } = await est().rpc("stale_estimate_count");
      if (error) throw { code: "upstream_error", message: error.message };
      return data;
    },
  });
}
